import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

import {
  CheckCircle,
  CircleNotch,
  GitMerge,
  Info,
  Trash,
  UsersThree,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const DuplicateRolesModal = ({ isOpen, onClose, organizationId, onMergeComplete }) => {
  const [loading, setLoading] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState({});
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (isOpen && organizationId) {
      fetchDuplicates();
    }
  }, [isOpen, organizationId]);

  const fetchDuplicates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org-chart/find-duplicates`,
        {},
        organizationId
      );

      if (response.ok) {
        const data = await response.json();
        setDuplicateGroups(data.duplicate_groups || []);
        
        // Initialize selected groups with suggested primary roles
        const initialSelection = {};
        data.duplicate_groups.forEach(group => {
          initialSelection[group.group_id] = {
            primaryRoleId: group.suggested_primary,
            duplicateRoleIds: group.roles
              .filter(r => r.role_id !== group.suggested_primary)
              .map(r => r.role_id),
            shouldMerge: true
          };
        });
        setSelectedGroups(initialSelection);
      } else {
        throw new Error('Failed to fetch duplicates');
      }
    } catch (err) {
      console.error('Error fetching duplicates:', err);
      setError('Failed to load duplicate roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrimaryRoleChange = (groupId, roleId) => {
    setSelectedGroups(prev => {
      const group = duplicateGroups.find(g => g.group_id === groupId);
      const allRoleIds = group.roles.map(r => r.role_id);
      
      return {
        ...prev,
        [groupId]: {
          primaryRoleId: roleId,
          duplicateRoleIds: allRoleIds.filter(id => id !== roleId),
          shouldMerge: prev[groupId]?.shouldMerge ?? true
        }
      };
    });
  };

  const handleToggleMerge = (groupId) => {
    setSelectedGroups(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        shouldMerge: !prev[groupId]?.shouldMerge
      }
    }));
  };

  const handleMergeAll = async () => {
    try {
      setMerging(true);
      setError(null);
      setSuccess(null);

      const groupsToMerge = Object.entries(selectedGroups)
        .filter(([_, selection]) => selection.shouldMerge && selection.duplicateRoleIds.length > 0);

      if (groupsToMerge.length === 0) {
        setError('No groups selected for merging');
        return;
      }

      let totalMerged = 0;
      let totalOpportunitiesUpdated = 0;

      for (const [groupId, selection] of groupsToMerge) {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/org-chart/merge-roles`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              primary_role_id: selection.primaryRoleId,
              duplicate_role_ids: selection.duplicateRoleIds,
              merge_strategy: 'merge_data'
            })
          },
          organizationId
        );

        if (response.ok) {
          const data = await response.json();
          totalMerged += data.deleted_count || 0;
          totalOpportunitiesUpdated += data.opportunities_updated || 0;
        } else {
          throw new Error(`Failed to merge group ${groupId}`);
        }
      }

      setSuccess(`Successfully merged ${totalMerged} duplicate roles and updated ${totalOpportunitiesUpdated} opportunities!`);
      
      // Wait a bit to show success message, then refresh
      setTimeout(() => {
        if (onMergeComplete) {
          onMergeComplete();
        }
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Error merging roles:', err);
      setError(err.message || 'Failed to merge roles. Please try again.');
    } finally {
      setMerging(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-4xl max-h-[90vh] bg-[#1a1a1a] rounded-2xl border border-[rgba(255,255,255,0.1)] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.1)]">
          <div className="flex items-center gap-3">
            <GitMerge size={24} className="text-[#6366F1]" />
            <div>
              <h2 className="text-xl font-bold text-white">Smart Role Deduplication</h2>
              <p className="text-sm text-gray-400">Merge duplicate roles to keep your org chart clean</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] px-6 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <CircleNotch size={40} className="text-[#6366F1] animate-spin" />
              <p className="text-gray-400">Analyzing roles for duplicates...</p>
            </div>
          ) : error ? (
            <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <WarningCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-500 font-semibold">Error</p>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>
          ) : success ? (
            <div className="flex items-start gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-500 font-semibold">Success!</p>
                <p className="text-green-400 text-sm">{success}</p>
              </div>
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <CheckCircle size={48} className="text-green-500" />
              <p className="text-xl font-semibold text-white">No Duplicates Found!</p>
              <p className="text-gray-400">Your org chart looks clean and organized.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info banner */}
              <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <Info size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-500 font-semibold">How Smart Merging Works</p>
                  <p className="text-blue-400 text-sm mt-1">
                    We've identified {duplicateGroups.length} groups of potentially duplicate roles. 
                    Select a primary role for each group, and we'll merge the duplicates while preserving 
                    all linked opportunities and supervisor relationships.
                  </p>
                </div>
              </div>

              {/* Duplicate groups */}
              {duplicateGroups.map((group) => (
                <div
                  key={group.group_id}
                  className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedGroups[group.group_id]?.shouldMerge ?? true}
                        onChange={() => handleToggleMerge(group.group_id)}
                        className="mt-1 w-4 h-4 rounded border-gray-600 text-[#6366F1] focus:ring-[#6366F1]"
                      />
                      <div>
                        <h3 className="text-white font-semibold">Duplicate Group ({group.roles.length} roles)</h3>
                        <p className="text-sm text-gray-400">{group.similarity_reason}</p>
                      </div>
                    </div>
                  </div>

                  {/* Role options */}
                  <div className="space-y-2 mt-3">
                    {group.roles.map((role) => {
                      const isPrimary = selectedGroups[group.group_id]?.primaryRoleId === role.role_id;
                      const isSelected = selectedGroups[group.group_id]?.shouldMerge ?? true;
                      
                      return (
                        <div
                          key={role.role_id}
                          className={`p-3 rounded-lg border transition-colors ${
                            isPrimary && isSelected
                              ? 'bg-[#6366F1]/10 border-[#6366F1]'
                              : 'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <input
                                type="radio"
                                name={`primary-${group.group_id}`}
                                checked={isPrimary}
                                onChange={() => handlePrimaryRoleChange(group.group_id, role.role_id)}
                                disabled={!isSelected}
                                className="mt-1 w-4 h-4 text-[#6366F1] focus:ring-[#6366F1]"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-white font-medium">{role.title}</p>
                                  {isPrimary && isSelected && (
                                    <span className="px-2 py-0.5 text-xs font-semibold bg-[#6366F1] text-white rounded">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                                  {role.employee_name && (
                                    <span className="flex items-center gap-1">
                                      <UsersThree size={14} />
                                      {role.employee_name}
                                    </span>
                                  )}
                                  {role.department && (
                                    <span>{role.department}</span>
                                  )}
                                  {role.linked_opportunities_count > 0 && (
                                    <span className="text-[#6366F1] font-semibold">
                                      {role.linked_opportunities_count} opportunities
                                    </span>
                                  )}
                                </div>
                                {role.description && (
                                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{role.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !success && duplicateGroups.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-[rgba(255,255,255,0.1)] bg-[rgba(0,0,0,0.3)]">
            <p className="text-sm text-gray-400">
              {Object.values(selectedGroups).filter(s => s.shouldMerge).length} groups selected for merging
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={merging}
                className="px-4 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMergeAll}
                disabled={merging || Object.values(selectedGroups).every(s => !s.shouldMerge)}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {merging ? (
                  <>
                    <CircleNotch size={16} className="animate-spin" />
                    Merging...
                  </>
                ) : (
                  <>
                    <GitMerge size={16} />
                    Merge Selected
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default DuplicateRolesModal;
